import React, { memo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Skeleton,
} from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import MetricPanel from "./MetricPanel";
import { formatDateOnly } from "../../../../Utils/formatDateTime";

const SKELETON_ROWS = Array.from({ length: 3 });

const PerformanceTable = ({
  data,
  loading,
  hasSearched,
  isDateRange,
  activeState,
  performanceCopy,
  bestWinRate,
  getWinRateValue,
  getPlaceRateValue,
}) => {
  const tableHeaders = [
    "Venue",
    isDateRange ? "Date Range" : "Venue Date",
    isDateRange ? "Avg Win Rate" : "Strike Win Rate",
    isDateRange ? "Avg Place Rate" : "Strike Place Rate",
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: "hidden",
        borderRadius: "12px",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      {/* Table Header Section */}
      <Box
        sx={{
          p: "16px",
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.5fr) auto" },
          alignItems: "start",
          background: "var(--color-surface)",
        }}
      >
        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: { xs: 12, sm: 14 },
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--color-text-muted)",
            }}
          >
            Intelligence Module
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: { xs: 20, sm: 24, lg: 28 },
              fontWeight: 700,
              lineHeight: 1,
              color: "var(--color-text)",
            }}
          >
            Model Performance
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: { xs: 12, sm: 14, lg: 16 },
              lineHeight: 1.55,
              color: "var(--color-text-muted)",
            }}
          >
            {performanceCopy}
          </Typography>
        </Box>

        {/* Status Badge */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifySelf: { xs: "flex-start", md: "flex-end" },
            gap: 1,
            p: "8px 12px",
            borderRadius: "9999px",
            background: activeState.background,
            color: activeState.color,
            border: activeState.border,
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: activeState.color,
            }}
          />
          <Typography
            sx={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            {activeState.label}
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          p: "0px 16px 16px 16px",
          overflowX: "auto",
          scrollbarWidth: "none",
          background: "var(--color-bg)",
        }}
      >
        <Table
          sx={{
            minWidth: 820,
            fontFamily: "'Space Grotesk', sans-serif",
            borderCollapse: "separate",
            borderSpacing: "0 12px",
            "& td, & th": {
              textAlign: "left",
            },
            "& th": {
              borderBottom: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeaders.map((head, index) => (
                <TableCell
                  key={head}
                  sx={{
                    p: "16px",
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: 10, sm: 12},
                    textTransform: "uppercase",
                    letterSpacing: "0.16em",
                    color: "var(--color-text-muted)",
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              // Skeleton loading rows
              SKELETON_ROWS.map((_, index) => (
                <TableRow sx={{ background: "var(--color-surface)", height: "70px", }} key={index}>
                  {Array.from({ length: 4 }).map((__, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        p: "16px",
                        "&:first-of-type": {
                          borderTopLeftRadius: "var(--radius-md)",
                          borderBottomLeftRadius: "var(--radius-md)",
                        },
                        "&:last-of-type": {
                          borderTopRightRadius: "var(--radius-md)",
                          borderBottomRightRadius: "var(--radius-md)",
                        },
                        border: "none",
                      }}
                    >
                      <Skeleton
                        variant="rounded"
                        animation="wave"
                        width={i === 0 ? "80%" : "60%"}
                        height={36}
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.04)",
                          borderRadius: "var(--radius-md)",
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length > 0 ? (
              // Data rows
              data.map((row, index) => {
                const winValue = getWinRateValue(row);
                const placeValue = getPlaceRateValue(row);
                const hasBestWinValue =
                  winValue !== null &&
                  winValue !== undefined &&
                  !isNaN(winValue) &&
                  Number(winValue) === bestWinRate;

                return (
                  <TableRow key={index}>
                    {/* Venue Name + Top Strike badge */}
                    <TableCell
                      sx={{
                        p: "16px",
                        background: hasBestWinValue ? "rgba(200, 255, 0, 0.12)" : "var(--color-surface)",
                        borderTopLeftRadius: "var(--radius-md)",
                        borderBottomLeftRadius: "var(--radius-md)",
                        border: hasBestWinValue ? "1px solid rgba(200, 255, 0, 0.25)" : "none",
                        borderRight: "none",
                      }}
                    >
                      <Box sx={{ display: "grid", gap: 1, alignItems: "start" }}>
                        <Typography
                          sx={{
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            fontSize: { xs: 14, sm: 16, lg: 18 },
                            fontWeight: 600,
                            color: "var(--color-text)",
                          }}
                        >
                          {row.venue_name || "N/A"}
                        </Typography>
                        {hasBestWinValue && (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              width: "fit-content",
                              p: "8px 12px",
                              borderRadius: "9999px",
                              background: "var(--color-text-light)",
                              color: "var(--color-text)",
                              border: "1px solid rgba(143, 149, 162, 0.25)",
                            }}
                          >
                            <Box
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                background: "var(--color-text)",
                              }}
                            />
                            <Typography
                              sx={{
                                fontFamily: "'Space Grotesk', sans-serif",
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                              }}
                            >
                              Top Strike
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>

                    {/* Date */}
                    <TableCell
                      sx={{
                        p: "16px",
                        background: hasBestWinValue ? "rgba(200, 255, 0, 0.12)" : "var(--color-surface)",
                        borderTop: hasBestWinValue ? "1px solid rgba(200, 255, 0, 0.25)" : "none",
                        borderBottom: hasBestWinValue ? "1px solid rgba(200, 255, 0, 0.25)" : "none",
                      }}
                    >
                      <Typography
                        className="tabular"
                        sx={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: { xs: 14, sm: 16, lg: 18 },
                          fontWeight: 600,
                          color: "var(--color-text)",
                        }}
                      >
                        {isDateRange
                          ? row.date_range || "N/A"
                          : row.venue_date
                            ? formatDateOnly(row.venue_date)
                            : "N/A"}
                      </Typography>
                    </TableCell>

                    {/* Win Rate */}
                    <TableCell
                      sx={{
                        textAlign: "right",
                        p: "16px",
                        background: hasBestWinValue ? "rgba(200, 255, 0, 0.12)" : "var(--color-surface)",
                        borderTop: hasBestWinValue ? "1px solid rgba(200, 255, 0, 0.25)" : "none",
                        borderBottom: hasBestWinValue ? "1px solid rgba(200, 255, 0, 0.25)" : "none",
                      }}
                    >
                      <MetricPanel
                        value={winValue}
                        color="var(--color-primary)"
                        metaLabel={isDateRange ? "average win" : "strike win"}
                      />
                    </TableCell>

                    {/* Place Rate */}
                    <TableCell
                      sx={{
                        textAlign: "right",
                        p: "16px",
                        background: hasBestWinValue ? "rgba(200, 255, 0, 0.12)" : "var(--color-surface)",
                        borderTopRightRadius: "var(--radius-md)",
                        borderBottomRightRadius: "var(--radius-md)",
                        border: hasBestWinValue ? "1px solid rgba(200, 255, 0, 0.25)" : "none",
                        borderLeft: "none",
                      }}
                    >
                      <MetricPanel
                        value={placeValue}
                        color="#00A3FF"
                        metaLabel={isDateRange ? "average place" : "strike place"}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              // Empty state
              <TableRow>
                <TableCell
                  colSpan={4}
                  sx={{
                    py: 0,
                    px: 0,
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                  }
                }>
                  <Box
                    sx={{
                      p: "16px",
                      borderRadius: "12px",
                      background: "var(--color-surface)",
                      display: "grid",
                      justifyItems: "center",
                      alignContent: "center",
                      textAlign: "center",
                      gap: 1,
                    }}
                  >
                    <CalendarMonth
                      sx={{ fontSize: 40, color: "var(--color-text-muted)" }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontSize: { xs: 16, sm: 18, lg: 20 },
                        fontWeight: 700,
                        color: "var(--color-text)",
                      }}
                    >
                      {hasSearched
                        ? "No performance data found"
                        : "Performance scan is idle"}
                    </Typography>
                    <Typography
                      sx={{
                        maxWidth: 500,
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: { xs: 12, sm: 14, lg: 16 },
                        lineHeight: 1.55,
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {hasSearched
                        ? "The current query returned no venue telemetry for this window. Adjust the scan range and run the search again."
                        : "Choose a date or date range, then launch the scan to compare model performance across venues."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default memo(PerformanceTable);
